import React from 'react';
import { useListContext } from 'framework/list';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  AccountWithProjects,
  DashboardProject,
  getProjectManager,
  HealthZone,
  PROJECT_ZONE_TEXT,
  PROJECT_ZONES,
  PROJECT_ZONES_ALIASES,
  ProjectSecurityContext,
} from 'domain/shared/newProject';
import type { ProjectZoneAlias } from '../dashboardTypes';
import { DashboardItem, DashboardItemData, DashboardZone } from 'framework/ui/dashboard';
import ProjectItemCollapsed from '../components/ProjectItemCollapsed';
import ProjectDashboardAccountItem from '../List/ProjectDashboardAccountItem';
import ProjectItemExpanded from '../components/ProjectItemExpanded';

type ProjectStageContentProps = {
  zone: ProjectZoneAlias;
};

type AccountWithProjectsAndId = AccountWithProjects &
  DashboardItemData & {
    accountHealthZone: HealthZone;
    id: string | number;
  };

const StaffingProjectItemContent = ({ project }: { project: DashboardProject }) => {
  const itemId = project.alias || project.id;
  const projectData = {
    ...project,
    projectManager: getProjectManager(project),
  };

  return (
    <DashboardItem
      sx={{ p: 0, backgroundColor: 'background.paper' }}
      itemId={itemId}
      collapsedComponent={
        <ProjectSecurityContext permissions={projectData.accessInfo}>
          <ProjectItemCollapsed project={projectData} />
        </ProjectSecurityContext>
      }
      expandedComponent={<ProjectItemExpanded projectId={projectData.id} />}
    />
  );
};

const ProjectDashboardCollapsibleZone = ({ zone }: ProjectStageContentProps) => {
  const listContext = useListContext();
  const theme = useTheme();

  const projectZoneAccentColor = {
    [PROJECT_ZONES_ALIASES.SAFE]: theme.palette.success.main,
    [PROJECT_ZONES_ALIASES.MONITORING]: theme.palette.warning.main,
    [PROJECT_ZONES_ALIASES.CRITICAL]: theme.palette.error.main,
    [PROJECT_ZONES_ALIASES.ESCALATION]: theme.palette.error.dark,
    [PROJECT_ZONES_ALIASES.OTHER]: theme.palette.primary.main,
  };

  const rawAccounts = (listContext?.data?.items || []) as Array<
    AccountWithProjects & {
      accountHealthZone: HealthZone;
    }
  >;

  const accounts = rawAccounts
    .map((account: any) => ({
      ...account,
      id: account.accountId,
    }))
    .filter((item: DashboardItemData) => {
      const project = item as AccountWithProjectsAndId;
      const backendZone = project.accountHealthZone || PROJECT_ZONES_ALIASES.SAFE;
      return backendZone === zone;
    }) as AccountWithProjectsAndId[];

  const countProjects = accounts.reduce((acc, item) => acc + item?.projects?.length, 0);

  const zoneConfig = {
    key: zone,
    title: PROJECT_ZONES[zone],
    description: PROJECT_ZONE_TEXT[zone],
    descriptionIcon:
      zone === PROJECT_ZONES_ALIASES.ESCALATION ? (
        <ErrorOutlineIcon color="error.dark" sx={{ fontSize: 18, mr: 0.75 }} />
      ) : null,
    accentColor: projectZoneAccentColor[zone],
    filterFn: (item: DashboardItemData) => {
      const project = item as AccountWithProjectsAndId;
      const backendZone = project.accountHealthZone || PROJECT_ZONES_ALIASES.SAFE;
      return backendZone === zone;
    },
  };

  const renderProjectItemContent = (project: DashboardProject) => {
    return <StaffingProjectItemContent project={project} />;
  };

  return (
    <DashboardZone
      zone={zoneConfig}
      items={accounts}
      defaultExpanded={true}
      customCount={countProjects}
      renderItem={(item: DashboardItemData) => {
        const account = item as AccountWithProjectsAndId;
        const projectCount = account.projects?.length || 0;

        if (projectCount === 1) {
          return renderProjectItemContent(account.projects[0]);
        }

        if (projectCount > 1) {
          return (
            <Box key={account.accountId} sx={{ mb: 1 }}>
              <ProjectDashboardAccountItem
                sx={{
                  backgroundColor: 'background.default',
                  '& .dashboard-item-container': { m: 0 },
                }}
                itemId={account.id.toString()}
                account={account}
                expandedComponent={
                  <>
                    {account.projects?.map(project => (
                      <React.Fragment key={project.id}>{renderProjectItemContent(project)}</React.Fragment>
                    ))}
                  </>
                }
              />
            </Box>
          );
        }

        return null;
      }}
    />
  );
};

export default ProjectDashboardCollapsibleZone;
